import React from 'react';
import { ArrowLeft, Save } from 'lucide-react';

interface ProjectWizardLayoutProps {
  children: React.ReactNode;
  currentStep: number;
  totalSteps: number;
  onPrevious?: () => void;
  onNext?: () => void;
  onSave?: () => void;
  canGoNext: boolean;
  canGoPrevious: boolean;
  isLoading?: boolean;
  title?: string;
}

export const ProjectWizardLayout: React.FC<ProjectWizardLayoutProps> = ({
  children,
  currentStep,
  totalSteps,
  onPrevious,
  onNext,
  onSave,
  canGoNext,
  canGoPrevious,
  isLoading = false,
  title = "Création de projet"
}) => {
  const progressPercentage = (currentStep / totalSteps) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-bold">C</span>
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">{title}</h1>
                <p className="text-sm text-gray-600">CRESS Guadeloupe</p>
              </div>
            </div>
            
            {onSave && (
              <button
                onClick={onSave}
                disabled={isLoading}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                aria-label="Sauvegarder le brouillon"
              >
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">Sauvegarder</span>
              </button>
            )}
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">
              Étape {currentStep} sur {totalSteps}
            </span>
            <span className="text-sm text-gray-500">
              {Math.round(progressPercentage)}% complété
            </span>
          </div>
          {/* Debug info */}
          <div className="text-xs text-gray-400 mb-2">
            Debug: currentStep={currentStep}, totalSteps={totalSteps}, canGoNext={canGoNext}
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
              style={{ width: `${progressPercentage}%` }}
              role="progressbar"
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`Progression: ${Math.round(progressPercentage)}%`}
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 min-h-[500px]">
          {children}
        </div>
      </main>

      {/* Footer Actions */}
      <footer className="bg-white border-t border-gray-200 px-6 py-4 sticky bottom-0">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <button
            onClick={onPrevious}
            disabled={!canGoPrevious || isLoading}
            className="flex items-center space-x-2 px-6 py-3 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Précédent</span>
          </button>

          <div className="flex items-center space-x-4">
            {isLoading && (
              <div className="flex items-center space-x-2 text-gray-500">
                <div className="w-4 h-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                <span className="text-sm">Sauvegarde...</span>
              </div>
            )}
            
            <button
              onClick={onNext}
              disabled={!canGoNext || isLoading}
              className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
            >
              {currentStep === totalSteps ? 'Finaliser' : currentStep === totalSteps - 1 ? 'Continuer vers le récapitulatif' : 'Suivant'}
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};